// ViewerPage.tsx — `/scans/:id`: one scan. Loads the Scan and its splats,
// follows /live while the scan is capturing or refining (SNAP replaces the
// cloud, DELT patches it by id, status updates the header), and lays out the
// model card beside the instrument cards: dimensions, cross-section,
// segmentation, details (rename/notes/tags), export and re-refine. HTTP and
// live splats are ordered by SplatLoadGuard (viewer/splatSync.ts): a stale
// HTTP body never replaces a newer SNAP or another scan's cloud, and the
// stored splats are reloaded when a followed scan settles. The camera
// trajectory (GET /trajectory) is reloaded whenever the keyframe count or
// status changes; the model card draws it and the scan visualization below
// the model compares each keyframe with the splats rendered from its camera.
// In the Segmentation card's Guided mode, useGuidedSegments tints the model
// card's cloud by segment, draws the seed points and claims its taps.
//
// Sharing (§6): the scan's `access` decides what shows. The owner gets Share
// (ShareDialog); others see their role (Viewer/Editor, Guest when signed out
// on a share link) and the owner. Viewers get no Segmentation card and a
// read-only Details card. Live status frames carry no access, so every
// update goes through mergeScan to keep it. A live-mode (§15.7) `handoff`
// message makes the Refinement card say it is refining from the live model.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { BsBoxArrowInRight, BsShare } from 'react-icons/bs';
import { Link, useParams } from 'react-router-dom';
import { Badge } from 'common';
import { ApiError, api, atLeast, mergeScan } from '../api';
import type { DimensionsResult, Scan, Trajectory } from '../api';
import { redirectToLogin } from '../hooks/useAuth';
import { ScanStatusBadge } from '../components/ScanStatusBadge';
import { errorMessage, fmtDate } from '../format';
import { LiveClient } from '../live';
import type { ProgressMessage } from '../live';
import { applyDelta, parseOSPL } from '../splat/format';
import type { SplatCloud } from '../splat/format';
import { CadAgentCard } from '../viewer/CadAgentCard';
import { DetailsCard } from '../viewer/DetailsCard';
import { DimensionsCard } from '../viewer/DimensionsCard';
import { ExportCard } from '../viewer/ExportCard';
import { ModelCard } from '../viewer/ModelCard';
import { RefineCard } from '../viewer/RefineCard';
import { ScanVizCard } from '../viewer/ScanVizCard';
import { SectionCard } from '../viewer/SectionCard';
import { SegmentCard } from '../viewer/SegmentCard';
import { ShareDialog } from '../viewer/ShareDialog';
import { SplatLoadGuard, isFollowed, shouldReloadSplats } from '../viewer/splatSync';
import { useGuidedSegments } from '../viewer/useGuidedSegments';
import { resolvePath } from '../viewer/trajectory';

export interface ViewerPageProps {
  // signedIn is false for a guest who opened a share link signed out.
  signedIn?: boolean;
}

export default function ViewerPage({ signedIn = true }: ViewerPageProps) {
  const { id = '' } = useParams();
  const [scan, setScanRaw] = useState<Scan | null>(null);
  // setScan keeps the caller's access across responses that omit it.
  const setScan = useCallback((s: Scan | null) => setScanRaw((prev) => (s ? mergeScan(prev, s) : null)), []);
  const [sharing, setSharing] = useState(false);
  const [needSignIn, setNeedSignIn] = useState(false);
  const [cloud, setCloud] = useState<SplatCloud | null>(null);
  const [dims, setDims] = useState<DimensionsResult | null>(null);
  const [progress, setProgress] = useState<ProgressMessage | null>(null);
  const [liveNote, setLiveNote] = useState('');
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [trajectory, setTrajectory] = useState<Trajectory | null>(null);
  const [activeCamera, setActiveCamera] = useState(0);
  const guard = useRef(new SplatLoadGuard());
  const idRef = useRef(id);
  idRef.current = id;

  const loadSplats = useCallback(async () => {
    const ticket = guard.current.begin(id);
    const accept = (what: string): boolean => {
      const v = guard.current.verdict(ticket, idRef.current);
      if (v !== 'ok') console.info(`[openscan/viewer] dropping HTTP ${what} for ${id}: ${v}`);
      return v === 'ok';
    };
    try {
      // version 2: §16 material when the scan has it (v1 otherwise).
      const buf = await api.getSplats(id, undefined, 2);
      const c = parseOSPL(buf);
      if (!accept(`splats (${c.count} gaussians)`)) return;
      console.info(`[openscan/viewer] loaded ${c.count} gaussians for ${id}${c.material ? ' (with material)' : ''}`);
      setCloud(c);
    } catch (err) {
      if (!accept('splats error')) return;
      if (err instanceof ApiError && err.status === 404) setCloud(null);
      else setError(errorMessage(err));
    }
  }, [id]);

  const loadDims = useCallback(async () => {
    try {
      setDims(await api.measureDimensions(id));
    } catch (err) {
      // No object segmented yet is a normal state, not a page error.
      console.info('[openscan/viewer] dimensions unavailable:', errorMessage(err));
      setDims(null);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setScan(null);
    setCloud(null);
    setDims(null);
    setNotFound(false);
    setNeedSignIn(false);
    api
      .getScan(id)
      .then((s) => {
        if (cancelled) return;
        setScan(s);
        if (s.has_splats || s.has_seed) void loadSplats();
        if (s.has_splats) void loadDims();
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
        else if (err instanceof ApiError && err.status === 401) setNeedSignIn(true);
        else setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id, loadSplats, loadDims, setScan]);

  // The camera path: new keyframes extend it, a finished refine swaps in the
  // bundle-adjusted cameras.
  const kfCount = scan?.keyframe_count ?? 0;
  const status = scan?.status;
  useEffect(() => {
    setTrajectory(null);
    setActiveCamera(0);
  }, [id]);
  useEffect(() => {
    if (!status || kfCount === 0) return;
    let cancelled = false;
    api
      .getTrajectory(id)
      .then((t) => {
        if (cancelled) return;
        console.info(`[openscan/viewer] trajectory for ${id}: ${t.cameras.length} cameras, refined=${t.refined}, ${t.cameras.filter((c) => c.blur).length} with modelled blur, rolling shutter ${t.rolling_shutter ?? 0}`);
        setTrajectory(t);
      })
      .catch((err) => {
        if (!cancelled) console.warn('[openscan/viewer] trajectory unavailable:', errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id, kfCount, status]);
  const path = useMemo(() => resolvePath(trajectory), [trajectory]);
  const active = Math.min(activeCamera, Math.max(0, path.length - 1));

  // Live updates only while the server still has work to stream.
  const following = isFollowed(scan?.status);
  useEffect(() => {
    setLiveNote('');
    if (!following) return;
    const live = new LiveClient(id, {
      onStatus: setScan,
      onProgress: setProgress,
      onLive: (m) => {
        if (m.phase !== 'handoff') return;
        console.info(`[openscan/viewer] live model handed to refinement${m.model_key ? ` (${m.model_key})` : ''}`);
        setLiveNote(m.message || 'Refining from the live model');
      },
      onSnapshot: (c) => {
        guard.current.noteSnapshot();
        setCloud(c);
      },
      onDelta: (d) => setCloud((prev) => (prev ? applyDelta(prev, d) : prev)),
    });
    return () => live.close();
  }, [id, following]);

  // When a followed scan settles, reload the stored splats (the socket may
  // close before a slow client got the final cloud) and measure the object.
  const prevStatus = useRef(scan?.status);
  useEffect(() => {
    const now = scan?.status;
    if (shouldReloadSplats(prevStatus.current, now)) {
      console.info(`[openscan/viewer] ${prevStatus.current} → ${now}; reloading stored splats and measuring`);
      void loadSplats();
      void loadDims();
    }
    prevStatus.current = now;
  }, [scan?.status, loadSplats, loadDims]);

  // Guided segmentation: the Segmentation card edits the guide, the model
  // card shows its tinted cloud and routes taps to it, Export lists segments.
  const onGuidedSegmented = useCallback(
    (d: DimensionsResult | null) => {
      setDims(d);
      void loadSplats();
    },
    [loadSplats],
  );
  const guided = useGuidedSegments({ scan, cloud, onScan: setScan, onSegmented: onGuidedSegmented, onError: setError });

  if (needSignIn) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Sign in to open this scan</Alert.Heading>
        <p>This share link is invalid, expired or revoked. If the scan was shared with your account, sign in to open it.</p>
        <Button variant="primary" onClick={redirectToLogin}>
          <BsBoxArrowInRight className="me-2" />
          Log In
        </Button>
      </Alert>
    );
  }
  if (notFound) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Scan not found</Alert.Heading>
        It may have been deleted, or it has not been shared with you. <Link to="/">Back to the library</Link>.
      </Alert>
    );
  }
  if (!scan) {
    return error ? (
      <Alert variant="danger">{error}</Alert>
    ) : (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const onSegmented = (d: DimensionsResult | null) => {
    setDims(d);
    void loadSplats();
  };
  const owner = atLeast(scan, 'owner');
  const editor = atLeast(scan, 'editor');
  const roleLabel = scan.access === 'editor' ? 'Editor' : 'Viewer';

  return (
    <>
      {signedIn && (
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
            Scans
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{scan.name}</Breadcrumb.Item>
        </Breadcrumb>
      )}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h1 className="h3 m-0">{scan.name}</h1>
        <ScanStatusBadge status={scan.status} />
        {!owner && (
          <>
            <Badge tone="info" data-testid="access-badge">
              {signedIn ? roleLabel : `Guest ${roleLabel.toLowerCase()}`}
            </Badge>
            <span className="small text-body-secondary">shared by {scan.owner}</span>
          </>
        )}
        <span className="small text-body-secondary ms-auto">
          {fmtDate(scan.created_at)} · {scan.keyframe_count} keyframes
        </span>
        {owner && scan.access !== undefined && (
          <Button size="sm" variant="outline-primary" onClick={() => setSharing(true)}>
            <BsShare className="me-1" />
            Share
          </Button>
        )}
      </div>
      {!signedIn && (
        <p className="small text-body-secondary">
          You are viewing a shared scan without signing in.{' '}
          <Button variant="link" size="sm" className="p-0 align-baseline" onClick={redirectToLogin}>
            <BsBoxArrowInRight className="me-1" />
            Sign in
          </Button>{' '}
          to see scans shared with your account.
        </p>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <div className="viewer-workspace">
        <div className="viewer-model">
          <ModelCard scan={scan} cloud={guided.displayCloud} path={path} activeCamera={active} onActiveCamera={setActiveCamera} tapTool={guided.tapTool} />
          <ScanVizCard scan={scan} cloud={cloud} path={path} active={active} onActive={setActiveCamera} rmsPx={trajectory?.rms_px} rollingShutter={trajectory?.rolling_shutter} />
        </div>
        <div className="viewer-tools">
          <DimensionsCard scan={scan} dims={dims} />
          <RefineCard scan={scan} progress={progress} onScan={setScan} onError={setError} signedIn={signedIn} liveNote={liveNote} />
          {scan.has_splats && <CadAgentCard scan={scan} onError={setError} signedIn={signedIn} />}
          <SectionCard scan={scan} cloud={cloud} />
          {editor && <SegmentCard key={scan.id} scan={scan} cloud={cloud} onSegmented={onSegmented} onError={setError} guided={guided} />}
          <DetailsCard scan={scan} onScan={setScan} onError={setError} />
          <ExportCard scan={scan} segments={guided.result?.segments} />
        </div>
      </div>
      {owner && (
        <ShareDialog
          scanId={scan.id}
          scanName={scan.name}
          show={sharing}
          onHide={() => setSharing(false)}
          onShared={(shared) => setScanRaw((prev) => (prev && prev.shared !== shared ? { ...prev, shared } : prev))}
        />
      )}
    </>
  );
}
